import type { ChangeEvent, ReactElement } from "react"
import { useState } from "react"

import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useDispatch, useSelector } from "react-redux"

import * as Yup from "yup"
import { ImageOutlined } from "@mui/icons-material"
import { Grid } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Button, InputText, Snackbar, TextArea, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik, useFormikContext } from "formik"
import { t } from "i18next"

import { SidAPI } from "../../../services/SidAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { CustomTokenRefresh } from "../../../types/generated/authentication/CustomTokenRefresh"
import type { Organization } from "../../../types/generated/authentication/Organization"
import { getEnvVar, validateFileFormat, validateFileSize } from "../../../utils/genericHelpers"
import { setAccessToken } from "../Slice"
import { useLogOut } from "../helpers"
import type { UserOrganizationFormikValues, UserOrganizationProps, createOrganizationProps } from "../interfaces"
import { InviteMembersContainer } from "./InviteMembers"

import styles from "./UserOrganization.module.scss"

/**
 * Component that renders the organization creation form and handles organization setup UI states
 *
 * @param {AxiosError} createOrganizationError - Error details if organization creation failed
 * @param {boolean} isRefreshTokenLoading - Indicates if token refresh is in progress
 * @param {number} maxFileSize - Maximum allowed file size for organization logo in bytes
 * @param {string | null} imgUrl - URL of the currently selected logo image
 * @param {Function} setImgUrl - Setter function for the logo image URL
 * @param {Function} setImgFile - Setter function for the logo image file
 * @returns {ReactElement} Organization creation form with state handling
 */
export function UserOrganization(props: Readonly<UserOrganizationProps>): ReactElement {
  const { createOrganizationError, isRefreshTokenLoading, maxFileSize, imgUrl, setImgUrl, setImgFile } = props

  const { t } = useTranslation("auth")

  const { values, errors, touched, isSubmitting, isValid, dirty, setFieldValue, handleChange, handleBlur, submitForm } =
    useFormikContext<UserOrganizationFormikValues>()

  const LogOut = useLogOut()

  const maxFileSizeInMB = Number(getEnvVar("ORGANIZATION_PROFILE_PICTURE_MAX_SIZE"))

  const onDrop = (acceptedFiles: File[]): void => {
    if (acceptedFiles.length === 0) return // Checks if there is an accepted file

    // stores actual file to be uploaded
    setImgFile(acceptedFiles[0])

    // stores an object Url of the image file to be displayed to the user in the form
    setImgUrl((prevImg) => {
      if (prevImg) URL.revokeObjectURL(prevImg) // removes old object url
      if (!acceptedFiles[0]) return null
      return URL.createObjectURL(acceptedFiles[0])
    })
  }

  const { open, getRootProps, getInputProps } = useDropzone({
    maxSize: maxFileSize, // in bytes
    multiple: false,
    accept: {
      "image/*": [".png", ".jpeg"],
    },
    noClick: true,
    noKeyboard: true,
    onDrop,
    validator: (file) => {
      return validateFileFormat(file, [".png", ".jpg", ".jpeg"]) || validateFileSize(file, maxFileSizeInMB)
    },
  })

  const handleDescriptionChange = (e: ChangeEvent): void => {
    if (
      values.orgDescription.length <= 120 &&
      (e as ChangeEvent<HTMLTextAreaElement>).target.value.toLocaleString().length <= 120
    )
      setFieldValue("orgDescription", (e as ChangeEvent<HTMLTextAreaElement>).target.value)
  }

  return (
    <Grid item container className={styles.mainContainer}>
      {/* Display network error if organization creation fails */}
      {Boolean(createOrganizationError) && createOrganizationError?.message === "Network Error" && (
        <Snackbar fullWidth severity="error" description={t("userOrganization.error")} />
      )}

      <Grid container className={styles.formContainer}>
        <Grid item container xs={12} justifyContent="start" gap={0.5}>
          <Typography variant="h1-bold">{t("userOrganization.title")}</Typography>

          <Typography variant="p" variantColor={2}>
            {t("userOrganization.subtitle")}
          </Typography>
        </Grid>

        <Grid container xs={12} gap={1}>
          <Grid item xs={12}>
            <Typography variant="label-bold">{t("userOrganization.logo.label")}</Typography>
          </Grid>

          <Grid item container xs={12} gap={2}>
            {imgUrl ? (
              <img src={imgUrl} alt="Organization Logo" className={styles.orgLogo} />
            ) : (
              <Grid className={styles.imgPlaceholder} onClick={open}>
                <ImageOutlined className={styles.imgIcon} />
              </Grid>
            )}

            <Grid item container xs={8} alignItems="start" gap={1}>
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <Button
                  id="upload"
                  variant="ghost"
                  size="small"
                  onClick={open}
                  disabled={isSubmitting || isRefreshTokenLoading}
                >
                  {imgUrl ? t("userOrganization.logo.reupload") : t("userOrganization.logo.upload")}
                </Button>
              </div>

              <Typography variant="label-bold" variantColor={2}>
                .PNG, .JPEG files up to {maxFileSizeInMB}MB.
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* @ts-expect-error */}
        {Boolean(createOrganizationError) && createOrganizationError?.response?.data?.name[0] && (
          <Grid item xs={12}>
            <Snackbar
              fullWidth
              severity="error"
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              description={createOrganizationError?.response?.data?.name[0]}
            />
          </Grid>
        )}

        <InputText
          fullWidth
          required
          id="name"
          label={t("userOrganization.name.label")}
          placeholder={t("userOrganization.name.placeholder")}
          description={t("userOrganization.name.unique")}
          helperText={`${values.name.length}/24`}
          value={values.name}
          error={Boolean(errors.name) && touched.name && errors.name}
          handleChange={(e: InputChangeEvent) => {
            if (values.name.length <= 24 && e.target.value.toLocaleString().length <= 24) handleChange(e)
          }}
          handleBlur={handleBlur}
          disabled={isSubmitting}
        />

        <TextArea
          hideDescription
          width={310}
          id="orgDescription"
          label={t("userOrganization.description.label")}
          placeholder={t("userOrganization.description.placeholder")}
          helperText={`${values.orgDescription.length}/120`}
          value={values.orgDescription}
          error={Boolean(errors.orgDescription) && touched.orgDescription && errors.orgDescription}
          handleChange={handleDescriptionChange}
          handleBlur={handleBlur}
          disabled={isSubmitting}
        />

        <Grid container item xs={12} gap={1.5}>
          <Button
            fullWidth
            id="create-organization"
            variant="primary"
            type="submit"
            onClick={() => submitForm()}
            disabled={
              isSubmitting ||
              isRefreshTokenLoading ||
              !(isValid || dirty) ||
              !validationSchema.isValidSync({
                name: values.name,
                orgDescription: values.orgDescription,
              })
            }
            isLoading={isSubmitting || isRefreshTokenLoading}
          >
            {t("userOrganization.submit")}
          </Button>

          <Button
            fullWidth
            variant="secondary"
            onClick={async () => {
              await SidAPI.logOut({ all_devices: false }).catch(console.error)
              LogOut({ redirect: true })
            }}
            disabled={isSubmitting}
          >
            {t("userOrganization.logout")}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const validationSchema = Yup.object({
  name: Yup.string()
    .required(t("userOrganization.validation.nameRequired", { ns: "auth" }))
    .min(1, t("userOrganization.validation.nameEmpty", { ns: "auth" }))
    .max(24, t("userOrganization.validation.nameMaxLength", { ns: "auth" })),
  orgDescription: Yup.string().max(120, t("userOrganization.validation.descriptionMaxLength", { ns: "auth" })),
})

/**
 * Container component that manages the organization creation state and form submission logic.
 * Handles both organization creation and member invitation flows.
 *
 * @returns {ReactElement} The organization creation form or member invitation screen
 */
export function UserOrganizationContainer(): ReactElement {
  const { t } = useTranslation("auth")

  const dispatch = useDispatch()

  // Maximum file size for organization logo
  const maxFileSize = Number(getEnvVar("ORGANIZATION_PROFILE_PICTURE_MAX_SIZE")) * 1024 * 1024

  // State management for logo image
  const [imgUrl, setImgUrl] = useState<string | null>(null)
  const [imgFile, setImgFile] = useState<File | undefined>(undefined)

  // Track current step in organization setup flow
  const [activeStep, setActiveStep] = useState<number>(0)

  const refreshToken = useSelector((state: RootState) => state.auth.refreshToken)

  // Mutation for creating new organization
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const createOrganizationMutation = useMutation<AxiosResponse<Organization>, AxiosError<any>, createOrganizationProps>(
    (props: createOrganizationProps) => {
      return SidAPI.createOrganization({
        name: props.name,
        description: props.description,
        logo: props.logo,
      })
    },
    {
      onSuccess: () => {
        // Refresh the access token to update the organization_id and organization_name fields in the payload
        refreshTokenMutation.mutateAsync(refreshToken as string)
      },
      onError(e) {
        formik.setErrors(e.response?.data)
      },
    },
  )

  // TODO: Handle errors when refreshing token fails
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const refreshTokenMutation = useMutation<AxiosResponse<CustomTokenRefresh>, AxiosError<any>, string>(
    SidAPI.refreshToken,
    {
      onSuccess: async (response) => {
        dispatch(setAccessToken(response.data.access))

        if (imgUrl) URL.revokeObjectURL(imgUrl)

        // Goes into next step which is the invite members step
        setActiveStep(1)
      },
      onError(e) {
        formik.setErrors(e.response?.data)
      },
    },
  )

  // Initialize Formik form with validation
  const formik = useFormik({
    initialValues: {
      name: "",
      orgDescription: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setStatus }) => {
      try {
        await createOrganizationMutation.mutateAsync({
          name: values.name,
          description: values.orgDescription,
          logo: imgFile,
        })
      } catch (e) {
        setStatus({
          error: t("userOrganization.createError"),
        })
      }
    },
  })

  // Render organization creation form or member invitation screen based on active step
  return activeStep === 0 ? (
    <FormikProvider value={formik}>
      <UserOrganization
        createOrganizationError={createOrganizationMutation.error}
        isRefreshTokenLoading={refreshTokenMutation.isLoading}
        maxFileSize={maxFileSize}
        imgUrl={imgUrl}
        setImgUrl={setImgUrl}
        setImgFile={setImgFile}
      />
    </FormikProvider>
  ) : (
    <InviteMembersContainer org={createOrganizationMutation.data?.data} />
  )
}
